import { detectGlobalContext } from "./globalContextEngine";
import { detectStructure } from "./structureEngine";
import { validateMarketNarrative } from "./marketNarrativeEngine";
import { updateBOSState } from "./bosStateEngine";
import { detectPhase3Signal } from "./signalEngine";
import { buildTrackedSignal, updateResultTracker } from "./resultTracker";
import { detectSupplyDemandZones } from "./zoneEngine";
import { getPendingTrackedZone } from "./zoneOutcomeTracker";
import {
  detectFallbackOrderBlock,
  detectOrderBlock,
  updateOrderBlockState,
} from "./orderBlockEngine";

export function runMasterPipeline({
  candlesM1,
  candlesM5,
  candlesM15,
  candlesM30,
  market,
  symbol,
  timeframe,
  prevResult,
  slPips,
}) {
  if (!candlesM1?.length) {
    return null;
  }

  const globalContext = detectGlobalContext(
    {
      M5: candlesM5,
      M15: candlesM15,
      M30: candlesM30,
    },
    market
  );

  const structure = detectStructure(candlesM1, market);
  const zoneAnalysis = detectSupplyDemandZones(candlesM1, market);
  const selectedZone = selectPipelineZone({
    zoneAnalysis,
    structure,
    candles: candlesM1,
    market,
    globalContext,
  });
  const zoneState =
    preserveTrackedZoneSelection({
      selectedZone,
      structure,
      candles: candlesM1,
      market,
      symbol,
      timeframe,
      globalContext,
    }) || selectedZone;
  const { aligned: htfAlignment, reason: htfConflictReason } = evaluateHtfAlignment({
    structure,
    globalContext,
  });

  let narrative = null;
  if (structure?.event === "BOS") {
    narrative = validateMarketNarrative({
      market,
      structure,
      zoneState,
      candles: candlesM1,
    });
  }

  const bosState = updateBOSState({
    structure,
    zoneState,
    narrative,
    globalContext,
    candles: candlesM1,
    market,
    symbol,
    timeframe,
  });

  const effectiveConfidence = computeEffectiveConfidence({
    globalContext,
    htfAlignment,
    bosState,
  });

  let orderBlockState = null;
  if (structure?.event === "BOS" && structure?.bosCandleIndex != null) {
    const orderBlock = detectOrderBlock({
      candles: candlesM1,
      market,
      bosIndex: structure.bosCandleIndex,
    });

    orderBlockState = updateOrderBlockState({
      candles: candlesM1,
      orderBlock,
      bosIndex: structure.bosCandleIndex,
    });
  }

  if (!orderBlockState) {
    orderBlockState = detectFallbackOrderBlock(candlesM1, market);
  }

  let signalState = buildWaitingSignalState({
    structure,
    bosState,
    htfAlignment,
    htfConflictReason,
  });

  if (bosState?.state === "LOCKED" && htfAlignment) {
    signalState = detectPhase3Signal({
      candles: candlesM1,
      zoneState: bosState.zone || zoneState,
      orderBlockState,
      market,
    });
  }

  const trackingZone = bosState?.zone || zoneState || orderBlockState || null;
  const trackedSignal = signalState?.signal
    ? buildTrackedSignal({
        signalState,
        candles: candlesM1,
        market,
        slPips,
        structure,
        bosState,
        zoneState: trackingZone,
      })
    : prevResult?.signal || null;

  const result = updateResultTracker({
    candles: candlesM1,
    signal: trackedSignal,
    prevResult,
  });

  return {
    globalContext,
    htfAlignment,
    htfConflictReason,
    effectiveConfidence,
    structure,
    zoneAnalysis,
    zoneState,
    narrative,
    bosState,
    orderBlockState,
    signalState,
    result,
  };
}

function evaluateHtfAlignment({ structure, globalContext }) {
  if (!structure?.event) {
    return {
      aligned: true,
      reason: "",
    };
  }

  if (!globalContext?.valid || globalContext.direction === "NEUTRAL") {
    return {
      aligned: true,
      reason: "",
    };
  }

  if (
    (globalContext.direction === "BULLISH" && structure.direction === "BULLISH") ||
    (globalContext.direction === "BEARISH" && structure.direction === "BEARISH")
  ) {
    return {
      aligned: true,
      reason: "",
    };
  }

  return {
    aligned: false,
    reason: `HTF ${globalContext.direction.toLowerCase()} bias conflicts with ${structure.direction.toLowerCase()} structure`,
  };
}

function computeEffectiveConfidence({ globalContext, htfAlignment, bosState }) {
  const rawConfidence = globalContext?.biasConfidence || 0;

  if (!htfAlignment) {
    return Number((rawConfidence * 0.35).toFixed(2));
  }

  if (bosState?.state !== "LOCKED") {
    return Number((rawConfidence * 0.7).toFixed(2));
  }

  return rawConfidence;
}

function buildWaitingSignalState({
  structure,
  bosState,
  htfAlignment,
  htfConflictReason,
}) {
  if (bosState?.state === "LOCKED" && htfAlignment === false) {
    return {
      state: "WAIT",
      reason: htfConflictReason || "Waiting for higher-timeframe alignment",
    };
  }

  if (bosState?.state === "LOCKED") {
    return { state: "WAIT", reason: "Waiting for signal confirmation" };
  }

  if (structure?.event === "BOS") {
    const formattedBosPrice =
      typeof structure.bosPrice === "number" ? structure.bosPrice.toFixed(2) : "--";

    return {
      state: "WAIT",
      reason:
        bosState?.reason ||
        `BOS confirmed at ${formattedBosPrice}, waiting for zone and narrative lock`,
    };
  }

  return {
    state: "WAIT",
    reason: structure?.reason || "Waiting for BOS lock",
  };
}

function normalizeZoneForPipeline(zone, market) {
  if (!zone) {
    return null;
  }

  const normalizedMarket = String(market || "").toUpperCase();
  const expectedType = normalizedMarket === "CRASH" ? "SUPPLY" : "DEMAND";
  const zoneType = zone.type
    ? String(zone.type).toUpperCase()
    : zone.isDemand
    ? "DEMAND"
    : "SUPPLY";

  return {
    ...zone,
    exists: true,
    valid: zone.valid !== false && zone.failed !== true,
    type: zoneType,
    name: zone.name || expectedType,
    label: zone.label || zone.name || expectedType,
    originIndex:
      zone.originIndex ??
      zone.startIndex ??
      zone.breakoutIndex ??
      zone.obIndex ??
      null,
    originTime:
      zone.originTime ??
      zone.startTime ??
      zone.breakoutTime ??
      zone.timestamp ??
      null,
  };
}

function zoneMatchesStructure(zone, structure) {
  if (!zone?.valid || !structure?.event) {
    return false;
  }

  if (structure.direction === "BULLISH") {
    return zone.type === "DEMAND";
  }

  if (structure.direction === "BEARISH") {
    return zone.type === "SUPPLY";
  }

  return false;
}

function zoneMatchesHtf(zone, globalContext) {
  if (!zone?.valid || !globalContext?.valid || globalContext.direction === "NEUTRAL") {
    return true;
  }

  if (globalContext.direction === "BULLISH") {
    return zone.type === "DEMAND";
  }

  if (globalContext.direction === "BEARISH") {
    return zone.type === "SUPPLY";
  }

  return true;
}

function zoneSupportsNarrative(zone, candles, market) {
  if (!zone?.valid || !candles?.length) {
    return false;
  }

  const price = candles[candles.length - 1]?.close;
  const normalizedMarket = String(market || "").toUpperCase();

  if (price == null) {
    return false;
  }

  if (normalizedMarket === "BOOM") {
    return zone.type === "DEMAND" && zone.high < price;
  }

  if (normalizedMarket === "CRASH") {
    return zone.type === "SUPPLY" && zone.low > price;
  }

  return false;
}

function zoneExistsBeforeBos(zone, structure) {
  if (!zone?.valid || !structure?.event || structure?.bosCandleIndex == null) {
    return true;
  }

  const zoneOriginIndex =
    zone.originIndex ??
    zone.startIndex ??
    zone.breakoutIndex ??
    null;

  if (zoneOriginIndex == null) {
    return true;
  }

  return zoneOriginIndex < structure.bosCandleIndex;
}

function zoneIsDeeplyMitigated(zone, candles, market) {
  if (!zone?.valid || !candles?.length) {
    return false;
  }

  const price = candles[candles.length - 1]?.close;
  const normalizedMarket = String(market || "").toUpperCase();

  if (price == null) {
    return false;
  }

  if (normalizedMarket === "BOOM") {
    return price < zone.low;
  }

  if (normalizedMarket === "CRASH") {
    return price > zone.high;
  }

  return false;
}

function zoneIsTested(zone) {
  if (!zone) {
    return false;
  }

  return zone.tested === true || zone.state === "tested";
}

function sortZonesForPipeline(left, right, globalContext) {
  const leftHtfMatch = zoneMatchesHtf(left, globalContext) ? 1 : 0;
  const rightHtfMatch = zoneMatchesHtf(right, globalContext) ? 1 : 0;

  if (leftHtfMatch !== rightHtfMatch) {
    return rightHtfMatch - leftHtfMatch;
  }

  const stateWeight = {
    fresh: 3,
    tested: 2,
    failed: 1,
  };
  const leftWeight = stateWeight[left?.state] ?? 0;
  const rightWeight = stateWeight[right?.state] ?? 0;

  if (leftWeight !== rightWeight) {
    return rightWeight - leftWeight;
  }

  if ((right?.impulseDistance ?? 0) !== (left?.impulseDistance ?? 0)) {
    return (right?.impulseDistance ?? 0) - (left?.impulseDistance ?? 0);
  }

  return (right?.breakoutTime ?? 0) - (left?.breakoutTime ?? 0);
}

function getZoneBosDistance(zone, structure) {
  const bosIndex = structure?.bosCandleIndex;
  const zoneOriginIndex =
    zone?.originIndex ??
    zone?.startIndex ??
    zone?.breakoutIndex ??
    null;

  if (bosIndex == null || zoneOriginIndex == null) {
    return Number.POSITIVE_INFINITY;
  }

  const distance = bosIndex - zoneOriginIndex;

  return distance >= 0 ? distance : Number.POSITIVE_INFINITY;
}

function sortZonesForLock(left, right, structure, globalContext) {
  const leftHtfMatch = zoneMatchesHtf(left, globalContext) ? 1 : 0;
  const rightHtfMatch = zoneMatchesHtf(right, globalContext) ? 1 : 0;

  if (leftHtfMatch !== rightHtfMatch) {
    return rightHtfMatch - leftHtfMatch;
  }

  const leftDistance = getZoneBosDistance(left, structure);
  const rightDistance = getZoneBosDistance(right, structure);

  if (leftDistance !== rightDistance) {
    return leftDistance - rightDistance;
  }

  const stateWeight = {
    fresh: 3,
    tested: 2,
    failed: 1,
  };
  const leftWeight = stateWeight[left?.state] ?? 0;
  const rightWeight = stateWeight[right?.state] ?? 0;

  if (leftWeight !== rightWeight) {
    return rightWeight - leftWeight;
  }

  if ((right?.breakoutTime ?? 0) !== (left?.breakoutTime ?? 0)) {
    return (right?.breakoutTime ?? 0) - (left?.breakoutTime ?? 0);
  }

  if ((right?.qualityScore ?? 0) !== (left?.qualityScore ?? 0)) {
    return (right?.qualityScore ?? 0) - (left?.qualityScore ?? 0);
  }

  return (right?.impulseDistance ?? 0) - (left?.impulseDistance ?? 0);
}

function pickBestLockCandidate(zones, structure, globalContext) {
  if (!zones.length) {
    return null;
  }

  return [...zones].sort((left, right) => sortZonesForLock(left, right, structure, globalContext))[0];
}

function selectPipelineZone({
  zoneAnalysis,
  structure,
  candles,
  market,
  globalContext,
}) {
  const normalizedZones = (zoneAnalysis?.zones || [])
    .map((zone) => normalizeZoneForPipeline(zone, market))
    .filter(
      (zone) =>
        zone?.valid &&
        !zoneIsTested(zone) &&
        zoneExistsBeforeBos(zone, structure) &&
        !zoneIsDeeplyMitigated(zone, candles, market)
    )
    .sort((left, right) => sortZonesForPipeline(left, right, globalContext));

  if (!normalizedZones.length) {
    return null;
  }

  const structureMatchedZones = normalizedZones.filter(
    (zone) =>
      zoneMatchesStructure(zone, structure) &&
      zoneSupportsNarrative(zone, candles, market)
  );

  if (structureMatchedZones.length) {
    return pickBestLockCandidate(structureMatchedZones, structure, globalContext);
  }

  const directionalZoneCandidates = normalizedZones.filter((zone) =>
    zoneSupportsNarrative(zone, candles, market)
  );
  const directionalZone = pickBestLockCandidate(
    directionalZoneCandidates,
    structure,
    globalContext
  );

  if (directionalZone) {
    return directionalZone;
  }

  return pickBestLockCandidate(normalizedZones, structure, globalContext);
}

function preserveTrackedZoneSelection({
  selectedZone,
  structure,
  candles,
  market,
  symbol,
  timeframe,
  globalContext,
}) {
  const trackedZone = getPendingTrackedZone({ symbol, timeframe });

  if (!trackedZone) {
    return null;
  }

  if (
    structure?.event === "BOS" &&
    trackedZone.lockedBosCandleTime != null &&
    structure.bosCandleTime !== trackedZone.lockedBosCandleTime
  ) {
    return null;
  }

  const normalizedTrackedZone = normalizeZoneForPipeline(trackedZone, market);

  if (!normalizedTrackedZone?.valid) {
    return null;
  }

  if (zoneIsDeeplyMitigated(normalizedTrackedZone, candles, market)) {
    return null;
  }

  if (
    selectedZone?.id === normalizedTrackedZone.id &&
    selectedZone?.high === normalizedTrackedZone.high &&
    selectedZone?.low === normalizedTrackedZone.low
  ) {
    return selectedZone;
  }

  return {
    ...normalizedTrackedZone,
    label: normalizedTrackedZone.label || trackedZone.label,
  };
}
